import { CloudDownloadOutlined, DownOutlined } from "@ant-design/icons";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Alert, App, Button, Dropdown, Flex, Space, Typography } from "antd";
import { useNavigate } from "react-router";
import { api, unwrap } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n } from "@/i18n";
import { useConnections } from "../channels/hooks";
import { PlatformLabel } from "../channels/PlatformBadge";
import { PlatformSkus } from "./PlatformSkus";

/**
 * Listing toko (App Flow §7): what every connected store sells, as the
 * stores have it. Listings download by themselves once a store is connected
 * and map by code; the rest wait here to be mapped or made into SKUs.
 */
export function Listings() {
  const { t, formatNumber } = useI18n();
  const navigate = useNavigate();
  const { can } = useAuth();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const summary = useQuery({
    queryKey: ["platform-skus", "summary", "duplicates"],
    queryFn: () => unwrap(api.GET("/v1/catalog/platform-skus", { params: { query: { limit: 1, withDuplicates: true } } })),
  });
  const unmapped = summary.data?.unmapped ?? 0;
  const duplicateGroups = summary.data?.duplicateGroups ?? 0;
  // Stores with an API to pull from: not manual channels, not ones still waiting to connect.
  const syncable = (useConnections().data?.items ?? []).filter((c) => c.platform !== "manual" && c.status !== "connecting");
  const sync = useMutation({
    mutationFn: (connectionId?: string) => unwrap(api.POST("/v1/catalog/import", { params: { query: connectionId ? { connectionId } : {} } })),
    onSuccess: ({ started, skipped }) => {
      if (started > 0) void message.success(t("catalog.importStarted", { n: started }));
      else void message.info(t("catalog.importNone"));
      if (skipped > 0) void message.warning(t("catalog.importSkipped", { n: skipped }));
    },
    onError: (err) => void message.error(errorText(err)),
  });

  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Space orientation="vertical" size={0}>
          <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
            {t("listings.title")}
          </Typography.Title>
          <Typography.Text type="secondary">{t("listings.subtitle")}</Typography.Text>
        </Space>
        {can("catalog.manage") && (
          <Space.Compact>
            <Button icon={<CloudDownloadOutlined />} loading={sync.isPending} onClick={() => sync.mutate(undefined)}>
              {t("catalog.import")}
            </Button>
            {syncable.length > 0 && (
              <Dropdown
                trigger={["click"]}
                menu={{
                  items: syncable.map((c) => ({
                    key: c.id,
                    label: (
                      <Space orientation="vertical" size={0}>
                        {c.name || c.externalShopId}
                        <PlatformLabel platform={c.platform} />
                      </Space>
                    ),
                  })),
                  onClick: ({ key }) => sync.mutate(key),
                }}
              >
                <Button icon={<DownOutlined />} title={t("catalog.importOne")} aria-label={t("catalog.importOne")} disabled={sync.isPending} />
              </Dropdown>
            )}
          </Space.Compact>
        )}
      </Flex>
      {duplicateGroups > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          title={t("catalog.duplicateBanner", { n: formatNumber(duplicateGroups) })}
          description={t("catalog.duplicateBannerBody")}
          action={<Button onClick={() => navigate("/app/catalog/duplicates")}>{t("catalog.review")}</Button>}
        />
      )}
      <PlatformSkus unmappedCount={unmapped} />
    </>
  );
}
